import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface NavigationEvent {
  type: 'forward' | 'backward' | 'register' | 'error' | 'focus';
  fromId?: string;
  toId?: string;
  groupName?: string;
  timestamp: number;
  details?: any;
}

export interface DebugConfig {
  enabled: boolean;
  logToConsole: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  maxLogSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class InputNavigationService {
  private inputGroups: Map<string, string[]> = new Map();

  private _debugConfig: DebugConfig = {
    enabled: false,
    logToConsole: false,
    logLevel: 'info',
    maxLogSize: 100
  };

  private _navigationEvents: NavigationEvent[] = [];

  private _navigationEvent = new Subject<NavigationEvent>();
  private _debugState = new BehaviorSubject<{
    groups: { [groupName: string]: string[] },
    events: NavigationEvent[],
    config: DebugConfig
  }>({
    groups: {},
    events: [],
    config: this._debugConfig
  });

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.logDebug('info', 'InputNavigationService inicializado');
  }

  setDebugConfig(config: Partial<DebugConfig>): void {
    this._debugConfig = { ...this._debugConfig, ...config };
    this.logDebug('info', 'Configuración de depuración actualizada', this._debugConfig);
    this.updateDebugState();
  }

  /**
   * Obtiene la configuración actual de depuración
   */
  getDebugConfig(): DebugConfig {
    return { ...this._debugConfig };
  }

  /**
   * Obtiene un observable para monitorear eventos de navegación
   */
  get navigationEvents(): Observable<NavigationEvent> {
    return this._navigationEvent.asObservable();
  }

  /**
   * Obtiene un observable para monitorear el estado de depuración
   */
  get debugState(): Observable<{
    groups: { [groupName: string]: string[] },
    events: NavigationEvent[],
    config: DebugConfig
  }> {
    return this._debugState.asObservable();
  }

  /**
   * Navega al input especificado por ID
   */
  navigateToInput(inputId: string, fromId?: string, preventScroll = false): void {
    const nextInput = this.document.getElementById(inputId) as HTMLInputElement;

    if (nextInput) {
      nextInput.focus({ preventScroll });




      if (!fromId && this.document.activeElement) {
        const activeElement = this.document.activeElement as HTMLElement;
        fromId = activeElement.id || undefined;
      }

      this.recordNavigationEvent({
        type: 'forward',
        fromId,
        toId: inputId,
        timestamp: Date.now()
      });
    } else {
      console.warn(`Input con ID ${inputId} no encontrado para navegación`);
      this.recordNavigationEvent({
        type: 'error',
        fromId,
        toId: inputId,
        timestamp: Date.now(),
        details: `No se encontró el elemento con ID: ${inputId}`
      });
    }
  }

  /**
   * Navega al input especificado y coloca el cursor al final
   */
  navigateToInputEnd(inputId: string, fromId?: string): void {
    const input = this.document.getElementById(inputId) as HTMLInputElement;

    if (input) {
      input.focus();
      const length = input.value.length;

      setTimeout(() => {
        input.setSelectionRange(length, length);
      }, 0);

      this.recordNavigationEvent({
        type: 'backward',
        fromId,
        toId: inputId,
        timestamp: Date.now()
      });
    } else {
      this.recordNavigationEvent({
        type: 'error',
        fromId,
        toId: inputId,
        timestamp: Date.now(),
        details: `No se encontró el elemento con ID: ${inputId}`
      });
    }
  }

  /**
   * Registra un grupo de inputs para navegación secuencial
   */
  registerInputGroup(groupName: string, inputIds: string[]): void {
    this.inputGroups.set(groupName, inputIds);

    this.recordNavigationEvent({
      type: 'register',
      groupName,
      timestamp: Date.now(),
      details: { inputIds }
    });

    if (this._debugConfig.enabled) {
      const missingInputs = inputIds.filter(id => !this.document.getElementById(id));
      if (missingInputs.length > 0) {
        this.logDebug('warn', `Grupo ${groupName} registrado con inputs inexistentes`, missingInputs);
      }
    }
  }

  /**
   * Obtiene todos los IDs de un grupo de navegación
   */
  getGroupInputIds(groupName: string): string[] | undefined {
    return this.inputGroups.get(groupName);
  }

  /**
   * Obtiene información de todos los grupos registrados
   */
  getAllGroups(): { [groupName: string]: string[] } {
    const groups: { [groupName: string]: string[] } = {};
    this.inputGroups.forEach((value, key) => {
      groups[key] = [...value];
    });
    return groups;
  }

  /**
   * Navega al siguiente input dentro de un grupo
   */
  navigateToNextInGroup(groupName: string, currentInputId: string): void {
    const group = this.inputGroups.get(groupName);

    if (!group) {
      this.logDebug('error', `Grupo no encontrado: ${groupName}`);
      return;
    }

    const currentIndex = group.indexOf(currentInputId);

    if (currentIndex >= 0 && currentIndex < group.length - 1) {
      const nextId = group[currentIndex + 1];
      this.navigateToInput(nextId, currentInputId);
      this.logDebug('debug', `Navegación dentro del grupo ${groupName}`, {
        from: currentInputId,
        to: nextId,
        currentIndex,
        nextIndex: currentIndex + 1
      });
    } else if (currentIndex === group.length - 1) {
      this.logDebug('info', `Final del grupo ${groupName} alcanzado`, {
        currentId: currentInputId
      });
    } else {
      this.logDebug('warn', `Input actual no encontrado en el grupo ${groupName}`, {
        currentId: currentInputId,
        groupInputs: group
      });
    }
  }

  /**
   * Navega al input anterior dentro de un grupo
   */
  navigateToPrevInGroup(groupName: string, currentInputId: string): void {
    const group = this.inputGroups.get(groupName);

    if (!group) {
      this.logDebug('error', `Grupo no encontrado: ${groupName}`);
      return;
    }

    const currentIndex = group.indexOf(currentInputId);

    if (currentIndex > 0) {
      const prevId = group[currentIndex - 1];
      this.navigateToInputEnd(prevId, currentInputId);
      this.logDebug('debug', `Navegación hacia atrás dentro del grupo ${groupName}`, {
        from: currentInputId,
        to: prevId,
        currentIndex,
        prevIndex: currentIndex - 1
      });
    } else if (currentIndex === 0) {
      this.logDebug('info', `Inicio del grupo ${groupName} alcanzado`, {
        currentId: currentInputId
      });
    } else {
      this.logDebug('warn', `Input actual no encontrado en el grupo ${groupName}`, {
        currentId: currentInputId,
        groupInputs: group
      });
    }
  }

  /**
   * Registra un input individual para que pueda ser referenciado por el sistema de navegación
   */
  registerInput(inputId: string, options: { nextInputId?: string, prevInputId?: string, groupName?: string } = {}): void {
    const inputExists = !!this.document.getElementById(inputId);
    const nextExists = !options.nextInputId || !!this.document.getElementById(options.nextInputId);
    const prevExists = !options.prevInputId || !!this.document.getElementById(options.prevInputId);

    if (this._debugConfig.enabled) {
      if (!inputExists) {
        this.logDebug('warn', `Registrando input inexistente: ${inputId}`);
      }
      if (options.nextInputId && !nextExists) {
        this.logDebug('warn', `Input siguiente no encontrado: ${options.nextInputId}`);
      }
      if (options.prevInputId && !prevExists) {
        this.logDebug('warn', `Input anterior no encontrado: ${options.prevInputId}`);
      }
    }

    if (options.groupName && options.nextInputId === undefined && options.prevInputId === undefined) {
      if (!this.inputGroups.has(options.groupName)) {
        this.inputGroups.set(options.groupName, [inputId]);
        this.logDebug('debug', `Nuevo grupo creado al registrar input: ${options.groupName}`);
      } else {
        const currentGroup = this.inputGroups.get(options.groupName) || [];
        if (!currentGroup.includes(inputId)) {
          this.inputGroups.set(options.groupName, [...currentGroup, inputId]);
          this.logDebug('debug', `Input agregado al grupo existente: ${options.groupName}`);
        }
      }
    }

    this.recordNavigationEvent({
      type: 'register',
      toId: inputId,
      timestamp: Date.now(),
      details: { options, exists: { input: inputExists, next: nextExists, prev: prevExists } }
    });
  }

  /**
   * Limpia el historial de eventos de navegación
   */
  clearNavigationHistory(): void {
    this._navigationEvents = [];
    this.logDebug('info', 'Historial de navegación limpiado');
    this.updateDebugState();
  }

  /**
   * Obtiene el historial de eventos de navegación
   */
  getNavigationHistory(): NavigationEvent[] {
    return [...this._navigationEvents];
  }

  /**
   * Obtiene el último evento de navegación
   */
  getLastNavigationEvent(): NavigationEvent | undefined {
    if (this._navigationEvents.length === 0) return undefined;
    return this._navigationEvents[this._navigationEvents.length - 1];
  }

  /**
   * Genera un informe de diagnóstico del estado actual del servicio
   */
  generateDiagnosticReport(): {
    timestamp: number;
    groups: { [groupName: string]: string[] };
    missingInputs: { [groupName: string]: string[] };
    recentEvents: NavigationEvent[];
    stats: {
      totalGroups: number;
      totalInputs: number;
      totalEvents: number;
      errorsCount: number;
    }
  } {
    const report = {
      timestamp: Date.now(),
      groups: this.getAllGroups(),
      missingInputs: {} as { [groupName: string]: string[] },
      recentEvents: this._navigationEvents.slice(-20),
      stats: {
        totalGroups: this.inputGroups.size,
        totalInputs: 0,
        totalEvents: this._navigationEvents.length,
        errorsCount: this._navigationEvents.filter(e => e.type === 'error').length
      }
    };

    this.inputGroups.forEach((inputs, groupName) => {
      report.stats.totalInputs += inputs.length;
      const missing = inputs.filter(id => !this.document.getElementById(id));
      if (missing.length > 0) {
        report.missingInputs[groupName] = missing;
      }
    });

    this.logDebug('info', 'Reporte de diagnóstico generado', report);
    return report;
  }

  /**
   * Registra un evento de navegación
   */
  private recordNavigationEvent(event: NavigationEvent): void {
    this._navigationEvents.push(event);

    if (this._navigationEvents.length > this._debugConfig.maxLogSize) {
      this._navigationEvents = this._navigationEvents.slice(-this._debugConfig.maxLogSize);
    }

    this._navigationEvent.next(event);

    this.updateDebugState();

    if (this._debugConfig.enabled && this._debugConfig.logToConsole) {
      const eventType = event.type.charAt(0).toUpperCase() + event.type.slice(1);
      if (event.type === 'error') {
        console.error(`[InputNavigation] ${eventType}:`, this.formatEventForConsole(event));
      } else {
        console.log(`[InputNavigation] ${eventType}:`, this.formatEventForConsole(event));
      }
    }
  }

  private formatEventForConsole(event: NavigationEvent): any {
    const { timestamp, ...rest } = event;
    const time = new Date(timestamp).toISOString().split('T')[1].substring(0, 12);
    return { time, ...rest };
  }

  private logDebug(level: 'error' | 'warn' | 'info' | 'debug' | 'trace', message: string, data?: any): void {
    if (!this._debugConfig.enabled) return;

    const logLevels = {
      'error': 0,
      'warn': 1,
      'info': 2,
      'debug': 3,
      'trace': 4
    };

    if (logLevels[level] <= logLevels[this._debugConfig.logLevel]) {
      if (this._debugConfig.logToConsole) {
        const prefix = `[InputNavigation] ${message}`;

        switch (level) {
          case 'error': console.error(prefix, data); break;
          case 'warn': console.warn(prefix, data); break;
          case 'info': console.info(prefix, data); break;
          case 'debug': console.debug(prefix, data); break;
          case 'trace': console.trace(prefix, data); break;
        }
      }
    }
  }

  /**
   * Actualiza el estado de depuración
   */
  private updateDebugState(): void {
    this._debugState.next({
      groups: this.getAllGroups(),
      events: [...this._navigationEvents],
      config: { ...this._debugConfig }
    });
  }

  focusFirstInput(groupName?: string): void {
    setTimeout(() => {
      if (groupName && this.inputGroups.has(groupName)) {
        const group = this.inputGroups.get(groupName);
        if (group && group.length > 0) {
          this.navigateToInput(group[0]);
        }
      } else {
        const autoFocusInput = document.querySelector('[autofocus]');
        if (autoFocusInput) {
          (autoFocusInput as HTMLElement).focus();
        } else {
          const firstInput = document.querySelector('form input:not([disabled]), mg-input:not([disabled])');
          if (firstInput) {
            const inputElement = firstInput.tagName === 'INPUT' ?
              firstInput : firstInput.querySelector('input');

            if (inputElement instanceof HTMLElement) {
              inputElement.focus();
            }
          }
        }
      }
    }, 200);
  }

  focusFirstInputInForm(formSelector: string): void {
    setTimeout(() => {
      const form = document.querySelector(formSelector);
      if (form) {
        const firstInput = form.querySelector('input:not([disabled]), mg-input:not([disabled])');
        if (firstInput) {
          const inputElement = firstInput.tagName === 'INPUT' ?
            firstInput : firstInput.querySelector('input');

          if (inputElement instanceof HTMLElement) {
            inputElement.focus();
          }
        }
      }
    }, 200);
  }
  navigateToComponent(componentId: string, fromId?: string): void {
    this.navigateToInput(componentId, fromId);

    setTimeout(() => {
      const component = document.getElementById(componentId);
      if (component && typeof component['focus'] === 'function') {
        component['focus']();
      }
    }, 50);
  }

  focusPhoneField(componentId: string, delay = 0): void {
    setTimeout(() => {

      const inputField = document.getElementById(componentId);
      if (inputField instanceof HTMLElement) {
        inputField.focus();

        const mouseEvent = new MouseEvent('mousedown', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        inputField.dispatchEvent(mouseEvent);
        inputField.focus();
      } else {
        return
      }
    }, delay);
  }
}
